import { Location } from '@angular/common';
import { Component, HostListener, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { NoteTypeEnums } from 'src/app/enum/note-type-enums';
import { ProviderNoteStateEnum } from 'src/app/enum/provider-note-state-enum';
import { CustomListDropdown } from 'src/app/interfaces/custom-list/custom-list-dropdown.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ProviderNoteModel } from 'src/app/models/shopping/provider-note.model';
import { ProviderInvoiceSelectionTableModel } from 'src/app/models/tables/shopping/provider-invoice-selection-table.model';
import { ProviderService } from 'src/app/services/archive/provider.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { DateConverterService } from 'src/app/services/global-services/date-converter.service';
import { FormControlStateService } from 'src/app/services/global-services/form-control-state.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { NoteService } from 'src/app/services/invoicing/note.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { ProviderNoteService } from 'src/app/services/shopping/provider-note.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { ListProviderNoteRetentionComponent } from '../../provider-note-retention/list-provider-note-retention/list-provider-note-retention.component';
import { PrefixNoteNumberComponent } from '../prefix-note-number/prefix-note-number.component';

const PROVIDER_NOTE_STATE_CONFIG: Record<number, { label: string; severity: string; titleState: string; iconState: string }> = {
  [ProviderNoteStateEnum.open]: { label: 'Abierta', severity: 'success', titleState: 'Cerrar', iconState: 'pi pi-lock-open' },
  [ProviderNoteStateEnum.closed]: { label: 'Cerrada', severity: 'primary', titleState: 'Abrir', iconState: 'pi pi-lock' }
};

@Component({
  selector: 'app-register-provider-note',
  templateUrl: './register-provider-note.component.html',
  styleUrls: ['./register-provider-note.component.scss'],
  standalone: false
})
export class RegisterProviderNoteComponent implements OnInit, OnDestroy {

  @ViewChild('prefixNoteNumberModal') prefixNoteNumberModalComponent?: CustomModalComponent | null = null;
  @ViewChild('retentionsModal') retentionsModalComponent?: CustomModalComponent | null = null;
  @ViewChild('selectInvoice') invoiceSelectionModalComponent?: CustomModalComponent | null = null;

  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly branchService = inject(BranchService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly dateConverterService = inject(DateConverterService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly formControlStateService = inject(FormControlStateService);
  private readonly headerService = inject(HeaderService);
  private readonly location = inject(Location);
  private readonly noteService = inject(NoteService);
  private readonly providerService = inject(ProviderService);
  private readonly providerNoteService = inject(ProviderNoteService);
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly windowService = inject(WindowService);

  afectedInvoiceDisabled: boolean = false;
  ascendant: boolean = true;
  branchId: number = 0;
  columnSort: string | null = null;
  disabledForm: boolean = false;
  enterpriseCodeName: string = '';
  filtersInvoiceSelection: ColumnResponseModel[] = [];
  formProviderNote: FormGroup = new FormGroup({});
  formProviderNoteFooter: FormGroup = new FormGroup({});
  headersInvoiceSelection: ColumnResponseModel[] = [];
  iconState: string = "pi pi-lock-open";
  inputsForPrefixNoteNumberModal$ = new BehaviorSubject<any>({
    providerNoteModel: new ProviderNoteModel(),
    permissionUpdatable: false,
    disabledForm: false
  });
  inputsForRetentionsModal$ = new BehaviorSubject<any>({
    providerNoteModel: new ProviderNoteModel(),
    permissionUpdatable: false,
    disabledForm: false
  });
  inputsForSelectInvoiceModal$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    filters: [],
    showCheckbox: true,
    showRecover: true,
    actions: []
  });
  isFormView: boolean = false;
  isOpened: string | undefined = undefined;
  ivaType: string = '';
  limit: number = 10;
  listBranch: any[] = [];
  listNoteType: CustomListDropdown[] = [];
  listNoteTypeFiltered: CustomListDropdown[] = [];
  listProvider: any[] = [];
  nameWindow: string = 'Captura Notas Proveedores';
  output$ = new Subject<any>();
  page: number = 1;
  permissionUpdatable: boolean = false;
  prefixNumberInvoice: string = '';
  projectName = ProjectName.projectName;
  providerId: number = 0;
  providerInvoiceId: number = 0;
  providerInvoiceSelectionData: PagedResponseModel<ProviderInvoiceSelectionTableModel> = new PagedResponseModel<ProviderInvoiceSelectionTableModel>();
  providerNoteId: number = 0;
  providerNoteModel: ProviderNoteModel = new ProviderNoteModel();
  severityState: string = "danger";
  stateString: string = 'Abierto';
  subscription: Subscription;
  titleState: string = "Cerrar";
  updateListDetail: number = 0;
  url: string = 'provider-note';
  componentInvoiceSelection = CustomTableSelectComponent;
  componentPrefixNoteNumberModal = PrefixNoteNumberComponent;
  componentRetentionsModal = ListProviderNoteRetentionComponent;

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.validateForm(this.providerNoteModel);
    await this.getPermission();
    this.activatedRoute.params.subscribe(async (params) => {
      this.providerNoteId = params['id'] ? +params['id'] : 0;
      if (this.providerNoteId) {
        await this.getProviderNoteById();
      } else {
        await this.getFirstBranchByUserAuthenticated();
      }
    });
    this.subscription = this.output$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeInvoice(payload);
          break;
        case 'onFilterChange':
          this.onFilterChangeInvoice(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
    this.subscription?.unsubscribe();
  }

  changeBranch(branchId: number) {
    this.providerId = 0;
    this.branchId = branchId;
    this.providerNoteModel = new ProviderNoteModel();
    this.providerNoteModel.branchId = this.branchId;
    this.providerNoteModel.providerId = this.providerId;
    this.validateForm(this.providerNoteModel);
    this.location.replaceState(`/provider-note/register`);
  }

  async changePrefixNoteNumber() {
    const currentBranchId = this.formProviderNote.controls['branchId'].value;
    const currentProviderId = this.formProviderNote.controls['providerId'].value;
    let currentPrefix = this.formProviderNote.controls['prefix'].value;
    let currentNoteNumber = this.formProviderNote.controls['noteNumber'].value;

    if (currentPrefix?.trim() == '') currentPrefix = null;
    if (currentNoteNumber?.toString().trim() == '') currentNoteNumber = 0;

    if (currentBranchId == this.providerNoteModel.branchId &&
      currentProviderId == this.providerNoteModel.providerId &&
      currentPrefix == this.providerNoteModel.prefix &&
      currentNoteNumber == this.providerNoteModel.noteNumber) return;

    if (currentBranchId == null || currentBranchId <= 0) {
      this.toastHandlerService.showToast('warning', 'Debe seleccionar una sucursal válida.', 'Atención');
      return;
    }
    if (currentProviderId == null || currentProviderId <= 0) {
      this.toastHandlerService.showToast('warning', 'Debe seleccionar un proveedor válido.', 'Atención');
      return;
    }
    if (currentNoteNumber == null || currentNoteNumber <= 0) {
      this.toastHandlerService.showToast('warning', 'Debe ingresar un número de nota mayor a cero.', 'Atención');
      return;
    }

    try {
      const response = await firstValueFrom(
        this.providerNoteService.getProviderNoteByKeys(currentBranchId, currentProviderId, currentPrefix, currentNoteNumber)
      );

      if (response.providerNoteId === 0) {
        const accepted = await this.confirmDialogService.confirm(`Nota Proveedor ${response.prefix == null ? '' : response.prefix} ${response.noteNumber} no existe. Desea agregarla?`);
        if (accepted) {
          this.providerNoteId = 0;
          this.providerNoteModel = new ProviderNoteModel();
          this.providerNoteModel.providerNoteId = 0;
          this.providerNoteModel.branchId = response.branchId;
          this.providerNoteModel.providerId = response.providerId;
          this.providerNoteModel.prefix = response.prefix;
          this.providerNoteModel.noteNumber = response.noteNumber;
          this.providerNoteModel.noteType = NoteTypeEnums.creditNote;
          this.providerNoteModel.providerInvoiceId = this.providerInvoiceId;
          this.validateForm(this.providerNoteModel);
          this.location.replaceState(`/provider-note/register`);
        } else {
          this.resetContract();
        }
      } else {
        if (response.branchId != currentBranchId) {
          this.toastHandlerService.showToast('error', 'La nota proveedor se encuentra en otra sucursal.');
          this.resetContract();
          return;
        }
        this.providerNoteModel = response;
        this.providerNoteId = this.providerNoteModel.providerNoteId;
        this.validateForm(this.providerNoteModel);
        this.location.replaceState(`/provider-note/register/${this.providerNoteId}`);
      }
    } catch (error) {
      this.resetContract();
      console.error('Error al buscar la nota proveedor por claves.', error);
    }
  }

  changeProvider(providerId: number) {
    this.providerId = providerId;
    this.providerNoteModel = new ProviderNoteModel();
    this.providerNoteModel.branchId = this.branchId;
    this.providerNoteModel.providerId = this.providerId;
    this.validateForm(this.providerNoteModel);
    this.location.replaceState(`/provider-note/register`);
  }

  async changeState() {
    if (this.providerNoteId === 0) {
      this.toastHandlerService.showToast('warning', 'Debe crear el encabezado de la nota proveedor.', 'Atención');
      return;
    }
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error', `No tiene permisos para modificar notas proveedores. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    try {
      const response = await firstValueFrom(this.providerNoteService.changeStateAsync(this.providerNoteId));
      this.providerNoteModel = response;
      this.validateForm(this.providerNoteModel);
      const successMessage = response.state === ProviderNoteStateEnum.open ?
        'La nota proveedor fue abierta exitosamente.' : 'La nota proveedor fue cerrada exitosamente.';
      this.toastHandlerService.showToast('success', successMessage);
    } catch (error) {
      console.error('Error al cambiar el estado de la nota proveedor.', error);
    }
  }

  createNewProviderNote() {
    this.resetContract();
  }

  async deleteProviderNote() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para eliminar notas proveedores. Comuníquese con el administrador del sistema.'
      );
      return;
    }
    if (this.providerNoteId === 0) {
      this.toastHandlerService.showToast(
        'warning', 'Debe crear el encabezado de la nota proveedor.',
        'Atención'
      );
      return;
    }
    const accepted = await this.confirmDialogService.confirm(`¿Realmente desea eliminar la nota proveedor ${this.providerNoteModel.prefix} ${this.providerNoteModel.noteNumber}?`);
    if (accepted) {
      try {
        await firstValueFrom(this.providerNoteService.deleteProviderNote(this.providerNoteId));
        this.toastHandlerService.showToast(
          'success',
          'La nota proveedor fue eliminada exitosamente.'
        );
        this.createNewProviderNote();
      } catch (error) {
        console.error('Error al eliminar la nota proveedor.', error);
      }
    }
  }

  async getAllInvoiceSelectionAsync() {
    try {
      this.providerInvoiceSelectionData = await firstValueFrom(this.providerNoteService.getProviderInvoiceAsync(
        this.page, this.limit, this.columnSort, this.ascendant, this.branchId, this.providerId, this.filtersInvoiceSelection
      ));
      this.updateInputsForSelectInvoiceModal();
    } catch (error) {
      console.error('Error al obtener la lista de los equipos', error);
    }
  }

  async getAllLists(...listsToLoad: Array<'branches' | 'providers' | 'noteTypes'>) {
    try {
      const lists = {
        branches: async () => {
          this.listBranch = await firstValueFrom(this.branchService.getAllBranchesForSelectAsync());
        },
        providers: async () => {
          this.listProvider = await firstValueFrom(this.providerService.getAllProvidersForSelect());
        },
        noteTypes: async () => {
          this.listNoteType = await firstValueFrom(this.noteService.getAllNoteTypeForSelectsAsync());
        }
      };
      if (listsToLoad.length) {
        await Promise.all(listsToLoad.map(list => lists[list]()));
      } else {
        await Promise.all([
          lists.branches(),
          lists.providers(),
          lists.noteTypes()
        ]);
      }
      this.initNoteTypeOptions();
    } catch (error) {
      console.error('Error al obtener las listas.', error);
    }
  }

  async getButtonClick(buttonClick: any) {
    const branchId = this.formProviderNote.get("branchId")?.value;
    let consecutive = this.formProviderNote.get("consecutive")?.value;

    if (branchId == null || branchId == 0) {
      this.toastHandlerService.showToast('warning', 'Seleccione una sucursal.', 'Atención');
      return;
    }

    consecutive = consecutive == null ? 0 : consecutive;

    const actionMap: Record<string, () => Promise<ProviderNoteModel>> = {
      first: () => firstValueFrom(this.providerNoteService.getFirstProviderNoteByKeysAsync(branchId)),
      previus: () => firstValueFrom(this.providerNoteService.getPreviusProviderNoteByKeysAsync(branchId, consecutive)),
      next: () => firstValueFrom(this.providerNoteService.getNextProviderNoteByKeysAsync(branchId, consecutive)),
      last: () => firstValueFrom(this.providerNoteService.getLastProviderNoteByKeysAsync(branchId)),
    };
    const action = actionMap[buttonClick.target.id];
    if (!action) {
      this.resetContract();
      return;
    }
    try {
      const providerNote = await action();
      this.providerNoteId = providerNote.providerNoteId;
      this.providerNoteModel = providerNote;
      this.validateForm(this.providerNoteModel);
      this.location.replaceState(`/provider-note/register/${this.providerNoteId}`);
    } catch (error) {
      this.resetContract();
      console.error(`Error al buscar la nota proveedor (${buttonClick.target.id}).`, error);
    }
  }

  async getColumnsInvoiceSelection() {
    try {
      this.headersInvoiceSelection = await firstValueFrom(this.providerNoteService.getColumnsForProviderInvoiceAsync());
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getFirstBranchByUserAuthenticated() {
    try {
      const response = await firstValueFrom(this.branchService.getFirstBranchByUserAuthenticatedAsync());
      await this.changeBranch(response.branchId);
    } catch (error) {
      console.error('Error al obtener la sucursal del usuario.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'warning', `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`, 'Atención'
        );
      }
    } else {
      localStorage.clear();
    }
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
    await this.getAllLists();
  }

  async getProviderNoteById() {
    try {
      this.providerNoteModel = await firstValueFrom(this.providerNoteService.getProviderNoteById(this.providerNoteId));
      this.branchId = this.providerNoteModel.branchId;
      this.providerNoteId = this.providerNoteModel.providerNoteId;
      this.validateForm(this.providerNoteModel);
    } catch (error) {
      console.error('Error al buscar la nota proveedor.', error);
    }
  }

  getUpdateProviderNote(providerNote: ProviderNoteModel) {
    this.providerNoteModel = providerNote;
    this.validateForm(this.providerNoteModel);
  }

  goToBack(event?: Event) {
    event?.preventDefault();
    this.router.navigate(['/provider-note']);
  }

  initNoteTypeOptions() {
    this.listNoteTypeFiltered = this.listNoteType.filter(x => x.value == NoteTypeEnums.creditNote || x.value == NoteTypeEnums.debitNote);
  }

  async onInvoiceValueChange(value: any) {
    if (value === null) {
      const isProviderNoteOpen = this.providerNoteModel.providerNoteId == 0 || this.providerNoteModel.state == ProviderNoteStateEnum.open;
      if (!isProviderNoteOpen) {
        this.toastHandlerService.showToast(
          'error',
          'Para modificar la factura afectada, la nota proveedor debe estar abierta.'
        );
        return;
      }

      this.providerInvoiceId = 0;
      this.prefixNumberInvoice = '';
      this.enterpriseCodeName = '';
      this.ivaType = '';
      this.formProviderNote.controls['providerInvoiceId'].patchValue(null);
    }
  }

  async onFilterChangeInvoice(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);
    this.filtersInvoiceSelection = this.removeDuplicates(filters);
    this.filtersInvoiceSelection.forEach((filter: any) => this.processFilter(filter));
    this.updateInputsForSelectInvoiceModal();
  }

  async onPageChangeInvoice(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getAllInvoiceSelectionAsync();
  }

  async openInvoiceSelectionModal() {
    if ((this.providerNoteModel.providerNoteId != 0 && this.providerNoteModel.state == ProviderNoteStateEnum.open) || this.providerNoteModel.providerNoteId == 0) {
      const branchId = this.formProviderNote.get("branchId")?.value;
      if (branchId && branchId != 0) {
        if (this.providerId == 0 || this.providerId == null) {
          this.toastHandlerService.showToast(
            'warning',
            'Seleccione un proveedor.'
          );
          return;
        }
        this.filtersInvoiceSelection = [];
        await Promise.all([
          this.getColumnsInvoiceSelection(),
          this.getAllInvoiceSelectionAsync()]
        );
        this.updateInputsForSelectInvoiceModal();
        this.invoiceSelectionModalComponent && this.invoiceSelectionModalComponent.show();
      } else {
        this.toastHandlerService.showToast(
          'warning',
          'Seleccione una sucursal.'
        );
        return;
      }
    } else {
      this.toastHandlerService.showToast(
        'error',
        'Para modificar la factura afectada, la nota proveedor debe estar abierta.'
      );
    }
  }

  async openPrefixNoteNumberModal() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para modificar notas proveedores, comuniquese con el administrador del sistema.`
      );
      return;
    }
    if (this.providerNoteId === 0) {
      this.toastHandlerService.showToast('warning', 'Debe crear el encabezado de la nota proveedor.', 'Atención');
      return;
    }
    this.inputsForPrefixNoteNumberModal$.next({
      providerNoteModel: this.providerNoteModel,
      permissionUpdatable: this.permissionUpdatable,
      disabledForm: this.disabledForm
    });
    this.prefixNoteNumberModalComponent?.show();
  }

  async openRetentionsModal() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para aplicar retenciones, comuniquese con el administrador del sistema.`
      );
      return;
    }
    this.inputsForRetentionsModal$.next({
      providerNoteModel: this.providerNoteModel,
      permissionUpdatable: this.permissionUpdatable,
      disabledForm: this.disabledForm
    });
    this.retentionsModalComponent?.show();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filtersInvoiceSelection = this.filtersInvoiceSelection.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filtersInvoiceSelection.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  async reloadProviderNote(params: any) {
    if (params.updateProviderNote) {
      await this.getProviderNoteById();
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  async resetContract() {
    this.providerNoteId = 0;
    this.providerNoteModel = new ProviderNoteModel();
    this.providerNoteModel.branchId = this.branchId;
    this.providerNoteModel.noteType = NoteTypeEnums.creditNote;
    this.validateForm(this.providerNoteModel);
    this.location.replaceState(`/provider-note/register`);
  }

  async send() {
    Object.keys(this.formProviderNote.controls).forEach((key) => {
      const control = this.formProviderNote.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formProviderNote.invalid) {
      this.showValidationMessages();
      return;
    }
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para crear o modificar notas proveedores. Comuníquese con el administrador del sistema.'
      );
      return;
    }

    const formValue = this.formProviderNote.getRawValue() as ProviderNoteModel;
    if (formValue.observation?.trim() === '') formValue.observation = null;

    if (this.providerNoteId === 0) {
      const newProviderNote = Object.assign(new ProviderNoteModel(), formValue);
      try {
        const response = await firstValueFrom(this.providerNoteService.createProviderNote(newProviderNote));
        this.toastHandlerService.showToast(
          'success',
          'La nota proveedor fue creada exitosamente.'
        );
        this.providerNoteModel = response;
        this.providerNoteId = this.providerNoteModel.providerNoteId;
        this.validateForm(this.providerNoteModel);
        this.location.replaceState(`/provider-note/register/${this.providerNoteId}`);
        this.isFormView = true;
      } catch (error) {
        console.error('Error al crear la nota proveedor.', error);
      }
    } else {
      Object.assign(this.providerNoteModel, formValue);
      try {
        const response = await firstValueFrom(this.providerNoteService.updateProviderNote(this.providerNoteModel));
        this.toastHandlerService.showToast('success', 'La factura afectada fue modificada exitosamente.', '');
        this.providerNoteModel = response;
        this.validateForm(this.providerNoteModel);
      } catch (error) {
        console.error('Error al actualizar la nota proveedor.', error);
      }
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      branchId: {
        required: 'El campo Sucursal es obligatorio.',
        min: 'Debe seleccionar una Sucursal válida.'
      },
      providerId: {
        required: 'El campo Proveedor es obligatorio.',
        min: 'Debe seleccionar un Proveedor válido.'
      },
      prefix: {
        maxlength: 'El campo Prefijo no puede tener más de 6 caracteres.'
      },
      noteNumber: {
        required: 'El campo Número Nota es obligatorio.',
        min: 'Debe ingresar un Número Nota superior a 0.'
      },
      consecutive: {
        required: 'El campo Consecutivo es obligatorio.'
      },
      date: {
        required: 'El campo Fecha es obligatorio.'
      },
      noteType: {
        required: 'El campo Tipo Nota es obligatorio.',
        min: 'Debe seleccionar un Tipo Nota válido.'
      },
      observation: {
        maxlength: 'El campo Observación no puede tener más de 90 caracteres.'
      },
      providerInvoiceId: {
        required: 'El campo Factura Afectada es obligatorio.',
        min: 'Debe seleccionar una Factura Afectada válida.'
      }
    };

    Object.keys(controlMessages).forEach(control => {
      const formControl = this.formProviderNote.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  async setInvoiceEnterprise(selectedItems: any) {
    const providerInvoice = selectedItems[0];
    this.prefixNumberInvoice = `${providerInvoice.prefix} - ${providerInvoice.invoiceNumber}`;
    this.providerInvoiceId = providerInvoice.providerInvoiceId;
    this.enterpriseCodeName = `${providerInvoice.enterpriseCode} - ${providerInvoice.enterpriseName}`;
    this.ivaType = `${providerInvoice.ivaType}`;
    this.formProviderNote.controls['providerInvoiceId'].patchValue(this.providerInvoiceId);
  }

  updateFormState() {
    if (!this.permissionUpdatable) {
      this.formProviderNote.disable();
    }
    if (this.providerNoteId !== 0) {
      this.isFormView = true;
    }
    const currentState = PROVIDER_NOTE_STATE_CONFIG[this.providerNoteModel.state]
      ?? PROVIDER_NOTE_STATE_CONFIG[ProviderNoteStateEnum.open];
    this.stateString = currentState.label;
    this.severityState = currentState.severity;
    this.titleState = currentState.titleState;
    this.iconState = currentState.iconState;
    if (this.providerNoteModel.state !== ProviderNoteStateEnum.open) {
      this.formProviderNote.disable();
      this.formControlStateService.setControlState(this.formProviderNote, ['branchId', 'providerId', 'prefix', 'noteNumber'], true);
      this.disabledForm = true;
    } else {
      this.formProviderNote.enable();
      this.disabledForm = false;
    }
    this.formControlStateService.setControlState(this.formProviderNote, ['consecutive'], false);
    this.formProviderNoteFooter.disable();
    this.updateListDetail++;
  }

  updateInputsForSelectInvoiceModal() {
    this.inputsForSelectInvoiceModal$.next({
      headers: this.headersInvoiceSelection,
      data: this.providerInvoiceSelectionData,
      filters: this.filtersInvoiceSelection,
      showCheckbox: true,
      showRecover: true,
      limitPaginator: true,
      actions: []
    });
  }

  validateForm(providerNote: ProviderNoteModel) {
    this.formProviderNote = this.formBuilder.group({
      branchId: [providerNote.branchId, [Validators.required, Validators.min(1)]],
      providerId: [providerNote.providerId, [Validators.required, Validators.min(1)]],
      consecutive: [providerNote.consecutive],
      noteType: [providerNote.noteType, [Validators.required, Validators.min(1)]],
      prefix: [providerNote.prefix, [Validators.maxLength(6)]],
      noteNumber: [providerNote.noteNumber, [Validators.required, Validators.min(1)]],
      providerInvoiceId: [providerNote.providerInvoiceId, [Validators.required, Validators.min(1)]],
      date: [providerNote.date ? this.dateConverterService.convertDatePreserveDay(providerNote.date) : null, [Validators.required]],
      observation: [providerNote.observation, [Validators.maxLength(90)]],
    });

    this.formProviderNoteFooter = this.formBuilder.group({
      subtotalValue: [providerNote.subtotalValue],
      ivaValue: [providerNote.ivaValue],
      retentionValue: [providerNote.retentionValue],
      paymentValue: [providerNote.paymentValue],
      residueValue: [providerNote.residueValue],
    });
    this.providerId = providerNote.providerId ?? 0;
    this.providerInvoiceId = providerNote.providerInvoiceId ?? 0;
    this.prefixNumberInvoice = providerNote.afectedProviderInvoice ?? '';
    this.ivaType = providerNote.ivaType ?? '';
    this.enterpriseCodeName = providerNote.enterpriseCodeName ?? '';
    this.updateFormState();
  }

}
